import {autoRegister, resolve} from "../../container";
import {type ChatResponse, GptChatService} from "./gptChatService";
import {SessionStorage} from "../../common/clientStorage";
import {Dictionary} from "../../page/elements/dictionary";
import {schedule} from "../../common/utils/promises";
import {LanguagesService} from "../../common/languages";
import {sanitizeHTML} from "../../common/sanitizer";
import {TrackingEventName} from "../../tracking/types";
import type {Maybe} from "../../bootstrap/common/types";

const JOB_URL_MARKDOWN_PATTERN = /\[([^[\]]+)]\((https?:\/\/[^\s)]+)\)/g; //example: [click me](https://www.example.com) <- label and link are in a group each without parenthesis
const JOB_HIGHLIGHT_MARKDOWN_PATTERN = /(\*{2}).+?(\*{2})/g; //example: **I am bold** <- highlighted text is in a group
const JOB_HEADLINE_MARKDOWN_PATTERN = /^#+ /gm;
const EMAIL_PATTERN = /([a-zA-Z0-9._%+-]{1,64}@[a-zA-Z0-9.-]{1,64}\.[a-zA-Z]{2,63})/g;

export type Message = UserMessage | AssistantMessage;

export type UserMessage = {
    role: Role.USER;
    content: string;
}

export type AssistantMessage = {
    role: Role.ASSISTANT;
    content: string;
    id?: string;
    reaction?: boolean;
}

export enum Role {
    ASSISTANT = "assistant",
    USER = "user",
}

const processSanitize: MessageProcessor = (message: string) => {
    return sanitizeHTML(message);
};

const processMarkdownLinks: MessageProcessor = (message: string) => {
    const markdownRefs = message.matchAll(JOB_URL_MARKDOWN_PATTERN);
    let processedMessage = message;
    for (const markdownRef of markdownRefs) {
        const clickLabel = markdownRef[1];
        const plainRef = markdownRef[2];

        const link = `<a href="${plainRef}" target="_blank" class="chat-message-link link"
            data-tracking-event-name="${TrackingEventName.TEXTLINK_INTERACT}"
            data-tracking-payload-text="${clickLabel}"
            data-tracking-payload-link="${plainRef}"
        >${clickLabel}</a>`;
        processedMessage = processedMessage.replace(markdownRef[0], link);
    }
    return processedMessage;
};

const processATags: MessageProcessor = (message: string) => {
    const messageElement = document.createElement("div");
    messageElement.innerHTML = message;

    const links = messageElement.querySelectorAll("a");
    links.forEach((link) => {
        link.classList.add("chat-message-link", "link");
        link.target = "_blank";
    });

    return messageElement.innerHTML;
};

const processMarkdownHighlighting: MessageProcessor = (message: string) => {
    const markdownHighlights = message.matchAll(JOB_HIGHLIGHT_MARKDOWN_PATTERN);
    let processedMessage = message;
    for (const markdownHighlight of markdownHighlights) {
        processedMessage = processedMessage.replace(markdownHighlight[1], "<b>");
        processedMessage = processedMessage.replace(markdownHighlight[2], "</b>");
    }
    return processedMessage;
};

const processMarkdownHeadline: MessageProcessor = (message: string) => {
    const markdownHeadlines = message.matchAll(JOB_HEADLINE_MARKDOWN_PATTERN);
    let processedMessage = message;
    for (const markdownHeadline of markdownHeadlines) {
        processedMessage = processedMessage.replace(markdownHeadline[0], "");
    }
    return processedMessage;
};

const processPlaintextEmails: MessageProcessor = (message: string) => {
    return message.replaceAll(EMAIL_PATTERN, (match) => {
        const isAlreadyLink = message.includes(`href="mailto:${match}"`);
        if (isAlreadyLink) {
            return match;
        }
        return `<a href="mailto:${match}" class="chat-message-link link"
            data-tracking-event-name="${TrackingEventName.TEXTLINK_INTERACT}"
            data-tracking-payload-text="${match}"
            data-tracking-payload-link="mailto:${match}"
        >${match}</a>`;
    });
};

class CompoundMessageProcessor {
    private processors: MessageProcessor[] = [];

    public addProcessor(processor: MessageProcessor): this {
        this.processors.push(processor);
        return this;
    }

    public process(message: string): string {
        let processedMessage = message;
        this.processors.forEach((processorCallback) => {
            processedMessage = processorCallback(processedMessage);
        });
        return processedMessage;
    }
}

export type StoredChatSession = {
    chatId: string;
    messages: Message[];
};

export class ChatSession {

    public constructor(
        private readonly storedSession: StoredChatSession
    ) {
    }

    public getMessage(): Message[] {
        return this.storedSession.messages;
    }

    public getChatId(): string {
        return this.storedSession.chatId;
    }
}

type MessageProcessor = (message: string) => string;

type ChatSessionUpdateCallback = (session: ChatSession) => void;

@autoRegister()
export class GptChatAppFactory {

    public constructor(
        private gptChatService: GptChatService = resolve(GptChatService),
        private sessionStorage: SessionStorage = resolve(SessionStorage),
        private languages: LanguagesService = resolve(LanguagesService)
    ) {
    }

    public create(configId: string, dictionary: Dictionary): GptChatApp {
        return new GptChatApp(this.gptChatService, this.sessionStorage, this.languages, configId, dictionary);
    }
}

export class GptChatApp {

    private messageProcessor = new CompoundMessageProcessor()
        .addProcessor(processSanitize)
        .addProcessor(processPlaintextEmails)
        .addProcessor(processMarkdownLinks)
        .addProcessor(processATags)
        .addProcessor(processMarkdownHighlighting)
        .addProcessor(processMarkdownHeadline);

    private chatSessionStorageKey: string;

    private newMessagesCallback: ChatSessionUpdateCallback[] = [];

    public constructor(
        private gptChatService: GptChatService,
        private sessionStorage: SessionStorage,
        private languages: LanguagesService,
        private configId: string,
        private dictionary: Dictionary
    ) {
        this.chatSessionStorageKey = "career-gpt-chat-session-" + this.configId + "-" + this.languages.activeLanguageId();
    }

    public onNewMessages(callback: ChatSessionUpdateCallback): void {
        this.newMessagesCallback.push(callback);
    }

    public async chat(userInput: string): Promise<void> {
        const session = this.sessionStorage.fetch<StoredChatSession>(this.chatSessionStorageKey);
        let messages = this.appendMessage(session?.chatId ?? "", {content: userInput, role: Role.USER});
        this.fireNewMessages(messages);

        let chatId: string;
        let message: Message;
        try {
            const response = session?.chatId ? await this.continueChat(session.chatId, userInput) : await this.createChat(userInput);
            chatId = response.chatId;
            message = {
                content: this.processMessage(response.assistantResponse, response.outcome),
                role: Role.ASSISTANT,
                id: response.assistantMessageId
            };
        } catch (_) {
            message = {
                content: this.dictionary.translate("CAREER_GPT_ERROR_MESSAGE"),
                role: Role.ASSISTANT
            };
        }
        messages = this.appendMessage(chatId!, message);
        this.fireNewMessages(messages);
    }

    public async addMessageReaction(messageId: string, reaction: boolean): Promise<void> {
        let session = this.sessionStorage.fetch<StoredChatSession>(this.chatSessionStorageKey);
        if (!session) {
            throw new Error("No session found in storage");
        }
        await this.gptChatService.addMessageReaction(session.chatId, messageId, reaction);
        session = this.sessionStorage.fetch<StoredChatSession>(this.chatSessionStorageKey);
        if (!session) {
            throw new Error("No session found in storage");
        }
        session.messages = session.messages.map((message): Message => {
            if (message.role === Role.ASSISTANT && message.id === messageId) {
                return {...message, reaction};
            }
            return message;
        });
        this.sessionStorage.save(this.chatSessionStorageKey, session);
        this.fireNewMessages(session);
    }

    public async addMessageFeedback(messageId: string, feedback: string): Promise<void> {
        const session = this.sessionStorage.fetch<StoredChatSession>(this.chatSessionStorageKey);
        if (!session) {
            throw new Error("No session found in storage");
        }
        await this.gptChatService.addUserFeedback(session.chatId, messageId, feedback);
    }

    private appendMessage(chatId: string, message: Message): StoredChatSession {
        const session = this.sessionStorage.fetch<StoredChatSession>(this.chatSessionStorageKey);
        if (!session) {
            throw new Error("No session found in storage");
        }
        session.chatId = chatId;
        session.messages = [...session.messages, message];
        this.sessionStorage.save(this.chatSessionStorageKey, session);
        return session;
    }

    private async createChat(message: string): Promise<ChatResponse> {
        return await schedule(this.gptChatService.createChat(this.configId, this.languages.activeLanguageId(), message)).as("create-chat");
    }

    private async continueChat(chatId: string, message: string): Promise<ChatResponse> {
        return await schedule(this.gptChatService.continueChat(chatId, message)).as("continue-chat");
    }

    private processMessage(message: Maybe<string>, responseOutcome: string): string {
        if (responseOutcome === "success") {
            return this.messageProcessor.process(message!!);
        } else if (responseOutcome === "content_too_long") {
            return this.dictionary.translate("CAREER_GPT_ERROR_MESSAGE_CONTENT_TOO_LONG");
        } else if (responseOutcome === "content_violation") {
            return this.dictionary.translate("CAREER_GPT_ERROR_MESSAGE_CONTENT_VIOLATION");
        } else if (responseOutcome === "bad_conduct_violation") {
            return this.dictionary.translate("CAREER_GPT_ERROR_MESSAGE_BAD_CONDUCT");
        } else if (responseOutcome === "company_policy_violation") {
            return this.dictionary.translate("CAREER_GPT_ERROR_MESSAGE_COMPANY_POLICY");
        } else if (responseOutcome === "internal_server_error") {
            return this.dictionary.translate("CAREER_GPT_ERROR_MESSAGE");
        } else {
            return this.dictionary.translate("CAREER_GPT_ERROR_MESSAGE");
        }
    }

    public init(welcomeMessage: string): void {
        let session = this.sessionStorage.fetch<StoredChatSession>(this.chatSessionStorageKey);
        if (!session) {
            session = {chatId: "", messages: [{content: welcomeMessage, role: Role.ASSISTANT}]};
        } else {
            // Else branch exists for backward compatibility for older assistantMessageId field. Remove after some time has passed since sprint 361.
            session.messages = session.messages.map(message => {
                if (message.role === Role.ASSISTANT && "assistantMessageId" in message && typeof message.assistantMessageId === "string") {
                    const {assistantMessageId, ...storedMessage} = message;
                    return {...storedMessage, id: message.id ?? assistantMessageId};
                }
                return message;
            });
        }
        this.sessionStorage.save(this.chatSessionStorageKey, session);
        this.fireNewMessages(session);
    }

    private fireNewMessages(storedSession: StoredChatSession): void {
        const session = new ChatSession(storedSession);
        this.newMessagesCallback.forEach(callback => callback(session));
    }
}